import { useEffect, useMemo, useRef } from 'react'
import Style from '@mui/icons-material/Style'
import { IconButton, Menu, MenuItem } from '@mui/material'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { VENDOR_INFO_TYPE_IDS } from '@/utils/info-types'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'

export type OptionsKeyType = keyof typeof options

const options: Record<string, string> = {
  approvals: 'Approvals',
  entrataVendorCard: 'Vendor Select',
  entrataPropertyCard: 'Property Select',
  vendorCard: 'Vendor Select',
  flags: 'Flags',
  fields: 'Fields',
}

export const ENTRATA_ORG_ID = '018b8be7-6182-4beb-24a1-4819669451c7'

export function useGetVisibleCardsState() {
  const { rootOrganization } = useCurrentAccountContext()
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()
  const { show_approvals_v2: showApprovalsV2 } = useFeatureFlagContext()

  const defaultVisibleCards = useMemo(() => {
    const visibleCards: OptionsKeyType[] = ['fields']
    if (rootOrganization?.id === ENTRATA_ORG_ID) {
      if (selectedWorkflowState?.name === 'Property Match') {
        visibleCards.unshift('entrataPropertyCard')
      } else if (selectedWorkflowState?.name === 'Vendor Match') {
        visibleCards.unshift('entrataVendorCard')
      }
    }
    if (selectedWorkflowState?.name === 'Match Vendor') {
      visibleCards.unshift('vendorCard')
    }
    return visibleCards
  }, [rootOrganization?.id, selectedWorkflowState?.name])

  const [savedVisibleCards, setVisibleCards] = useLocalStorage<
    OptionsKeyType[]
  >(
    `validation-visible-cards-${selectedWorkflowState?.id}`,
    defaultVisibleCards,
  )

  // The Approvals card can stay saved as visible after the show_approvals_v2
  // flag is turned off, so it is dropped here while the flag is off.
  const visibleCards = useMemo(
    () =>
      showApprovalsV2
        ? savedVisibleCards
        : savedVisibleCards.filter((card) => card !== 'approvals'),
    [savedVisibleCards, showApprovalsV2],
  )

  return [visibleCards, setVisibleCards] as const
}

export function useTurnOnApprovalsCard(turnOn: boolean, documentId?: string) {
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()
  const visibleCardsState = useGetVisibleCardsState()

  // Read through a ref so the effect runs only when a new document opens. If
  // it also ran whenever the saved cards changed, the user could never hide
  // the card again.
  const visibleCardsStateRef = useRef(visibleCardsState)
  visibleCardsStateRef.current = visibleCardsState

  useEffect(() => {
    if (!turnOn || !documentId) return
    const [visibleCards, setVisibleCards] = visibleCardsStateRef.current
    if (!visibleCards.includes('approvals')) {
      setVisibleCards([...visibleCards, 'approvals'])
    }
    // documentId and the workflow state id aren't used in this useEffect. It
    // fires when either changes: a new document, or the saved cards moving to
    // another workflow state's storage key.
  }, [turnOn, documentId, selectedWorkflowState?.id])
}

export default function VisibleCardsControl() {
  const { rootOrganization } = useCurrentAccountContext()
  const overlay = useOverlay()
  const { show_approvals_v2: showApprovalsV2 } = useFeatureFlagContext()

  const [visibleCards, setVisibleCards] = useGetVisibleCardsState()

  const { fieldsMap } = useProjectContext()

  const showVendorSelect = useMemo(() => {
    return Object.values(fieldsMap).some((field) =>
      VENDOR_INFO_TYPE_IDS.includes(field.info_type_id || ''),
    )
  }, [fieldsMap])

  if (!rootOrganization) {
    return null
  }

  return (
    <>
      <IconButton size="small" onClick={overlay.open}>
        <Style />
      </IconButton>
      {overlay.anchorEl && (
        <Menu
          open={overlay.isOpen}
          onClose={overlay.close}
          anchorEl={overlay.anchorEl as HTMLButtonElement}
          elevation={16}
        >
          {Object.entries(options).map(([key, value]) => {
            if (key === 'approvals' && !showApprovalsV2) {
              return null
            }

            if (
              ['entrataVendorCard', 'entrataPropertyCard'].includes(key) &&
              rootOrganization.id !== ENTRATA_ORG_ID
            ) {
              return null
            }

            // We only show the vendor select if we have a vendor field
            if (key === 'vendorCard' && !showVendorSelect) {
              return null
            }

            return (
              <MenuItem
                dense
                key={key}
                onClick={() => {
                  setVisibleCards((prev) => {
                    if (prev.includes(key as OptionsKeyType)) {
                      return prev.filter((item) => item !== key)
                    }
                    return [...prev, key] as OptionsKeyType[]
                  })
                }}
                selected={visibleCards.includes(key as OptionsKeyType)}
              >
                {value}
              </MenuItem>
            )
          })}
        </Menu>
      )}
    </>
  )
}
